import { Button, Card, Input } from "@omnichannel/ui";
import { useEffect, useState } from "react";
import { ApiError, request } from "./api";

export interface ProbeResultRow {
  id: string;
  ok: boolean;
  latency_ms: number | null;
  error: string | null;
  checked_at: string;
}

export interface MonitoredTargetRow {
  id: string;
  name: string;
  kind: "tcp" | "tls" | "http";
  host: string;
  port: number | null;
  path: string | null;
  expected_status: number | null;
  sni: string | null;
  enabled: boolean;
  interval_s: number;
  fail_threshold: number;
  current_state: "up" | "down" | "unknown";
  consecutive_failures: number;
  consecutive_successes: number;
  down_since: string | null;
  last_state_change_at: string | null;
  last_checked_at: string | null;
  uptime_24h_percent: number | null;
  recent_failures: ProbeResultRow[];
  created_at: string;
  updated_at: string;
}

export function Monitoring() {
  const [targets, setTargets] = useState<MonitoredTargetRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionNotice, setActionNotice] = useState<string | null>(null);

  // New target form state
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [kind, setKind] = useState<"tcp" | "tls" | "http">("tcp");
  const [host, setHost] = useState("");
  const [port, setPort] = useState<string>("24443");
  const [path, setPath] = useState("/");
  const [expectedStatus, setExpectedStatus] = useState("200");
  const [sni, setSni] = useState("");
  const [intervalS, setIntervalS] = useState("60");
  const [failThreshold, setFailThreshold] = useState("3");
  const [saving, setSaving] = useState(false);

  const loadTargets = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await request<MonitoredTargetRow[]>("/api/v1/monitoring/targets");
      setTargets(data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось загрузить список целей");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTargets();
  }, []);

  const handleSeed = async () => {
    setLoading(true);
    setError(null);
    setActionNotice(null);
    try {
      const res = await request<MonitoredTargetRow[]>("/api/v1/monitoring/targets/seed", {
        method: "POST",
      });
      setTargets(res);
      setActionNotice("Базовые цели успешно добавлены (seed выполнен)");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось выполнить seed");
    } finally {
      setLoading(false);
    }
  };

  const handleProbe = async (targetId: string) => {
    setError(null);
    setActionNotice(null);
    try {
      await request<ProbeResultRow>(`/api/v1/monitoring/targets/${targetId}/probe`, {
        method: "POST",
      });
      await loadTargets();
      setActionNotice("Проверка выполнена");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Ошибка выполнения проверки");
    }
  };

  const handleDelete = async (targetId: string, targetName: string) => {
    if (!confirm(`Удалить цель «${targetName}»?`)) return;
    try {
      await request(`/api/v1/monitoring/targets/${targetId}`, { method: "DELETE" });
      setTargets((prev) => prev.filter((t) => t.id !== targetId));
      setActionNotice(`Цель «${targetName}» удалена`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось удалить цель");
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = {
        name: name.trim(),
        kind,
        host: host.trim(),
        port: port ? parseInt(port, 10) : null,
        interval_s: parseInt(intervalS, 10) || 60,
        fail_threshold: parseInt(failThreshold, 10) || 3,
      };
      if (kind === "http") {
        payload.path = path.trim() || "/";
        payload.expected_status = expectedStatus ? parseInt(expectedStatus, 10) : 200;
      }
      if (kind === "tls" && sni.trim()) {
        payload.sni = sni.trim();
      }

      await request<MonitoredTargetRow>("/api/v1/monitoring/targets", {
        method: "POST",
        body: JSON.stringify(payload),
      });

      setShowForm(false);
      setName("");
      setHost("");
      setSni("");
      setActionNotice("Цель успешно добавлена");
      await loadTargets();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось создать цель");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: "20px 0" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2>Контроль серверов (HUB-MON)</h2>
        <div style={{ display: "flex", gap: 10 }}>
          <Button variant="secondary" onClick={loadTargets} disabled={loading}>
            Обновить
          </Button>
          <Button variant="secondary" onClick={handleSeed} disabled={loading}>
            Заполнить seed-цели
          </Button>
          <Button className="primary" onClick={() => setShowForm((prev) => !prev)}>
            {showForm ? "Скрыть форму" : "Добавить цель"}
          </Button>
        </div>
      </div>

      {actionNotice && <p style={{ color: "green", marginBottom: 12 }}>{actionNotice}</p>}
      {error && (
        <p className="error-text" role="alert" style={{ marginBottom: 12 }}>
          {error}
        </p>
      )}

      {showForm && (
        <Card style={{ marginBottom: 24, padding: 20 }}>
          <h3>Новая цель мониторинга</h3>
          <form onSubmit={handleCreate} style={{ display: "grid", gap: 12, gridTemplateColumns: "1fr 1fr" }}>
            <div>
              <label>Название цели</label>
              <Input
                placeholder="Например, DE-вход или Reality VPN"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div>
              <label>Тип проверки</label>
              <select
                value={kind}
                onChange={(e) => setKind(e.target.value as "tcp" | "tls" | "http")}
                style={{ width: "100%", padding: "10px", borderRadius: 4, border: "1px solid #ccc" }}
              >
                <option value="tcp">TCP</option>
                <option value="tls">TLS</option>
                <option value="http">HTTP</option>
              </select>
            </div>
            <div>
              <label>Хост / IP</label>
              <Input
                placeholder="37.235.241.94 или example.com"
                value={host}
                onChange={(e) => setHost(e.target.value)}
                required
              />
            </div>
            <div>
              <label>Порт</label>
              <Input
                type="number"
                placeholder="24443"
                value={port}
                onChange={(e) => setPort(e.target.value)}
                required={kind !== "http"}
              />
            </div>
            {kind === "tls" && (
              <div>
                <label>SNI (опционально)</label>
                <Input
                  placeholder="yahoo.com"
                  value={sni}
                  onChange={(e) => setSni(e.target.value)}
                />
              </div>
            )}
            {kind === "http" && (
              <>
                <div>
                  <label>Путь (HTTP path)</label>
                  <Input
                    placeholder="/health"
                    value={path}
                    onChange={(e) => setPath(e.target.value)}
                  />
                </div>
                <div>
                  <label>Ожидаемый статус (HTTP code)</label>
                  <Input
                    type="number"
                    placeholder="200"
                    value={expectedStatus}
                    onChange={(e) => setExpectedStatus(e.target.value)}
                  />
                </div>
              </>
            )}
            <div>
              <label>Интервал (сек, default 60)</label>
              <Input
                type="number"
                value={intervalS}
                onChange={(e) => setIntervalS(e.target.value)}
              />
            </div>
            <div>
              <label>Порог ошибок (fail threshold, default 3)</label>
              <Input
                type="number"
                value={failThreshold}
                onChange={(e) => setFailThreshold(e.target.value)}
              />
            </div>
            <div style={{ gridColumn: "span 2", display: "flex", gap: 10, marginTop: 10 }}>
              <Button className="primary" type="submit" disabled={saving}>
                {saving ? "Сохранение..." : "Создать цель"}
              </Button>
              <Button variant="secondary" type="button" onClick={() => setShowForm(false)}>
                Отмена
              </Button>
            </div>
          </form>
        </Card>
      )}

      {targets.length === 0 && !loading && (
        <p>Цели мониторинга пока не настроены. Нажмите «Заполнить seed-цели» для импорта серверов по умолчанию.</p>
      )}

      {targets.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>Статус</th>
              <th>Цель</th>
              <th>Тип</th>
              <th>Хост:Порт</th>
              <th>Аптайм (24 ч)</th>
              <th>Последняя проверка</th>
              <th>Последние падения</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {targets.map((t) => {
              const stateBadge =
                t.current_state === "up" ? (
                  <span style={{ color: "green", fontWeight: "bold" }}>✅ UP</span>
                ) : t.current_state === "down" ? (
                  <span style={{ color: "red", fontWeight: "bold" }}>⚠ DOWN</span>
                ) : (
                  <span style={{ color: "gray" }}>⏳ Ожидание</span>
                );

              return (
                <tr key={t.id}>
                  <td>{stateBadge}</td>
                  <td>
                    <strong>{t.name}</strong>
                    {t.down_since && (
                      <div style={{ fontSize: 12, color: "red" }}>
                        Недоступен с {new Date(t.down_since).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })}
                      </div>
                    )}
                  </td>
                  <td>{t.kind.toUpperCase()}</td>
                  <td>
                    <code>{t.host}{t.port ? `:${t.port}` : ""}</code>
                    {t.sni && <div style={{ fontSize: 12, color: "#666" }}>SNI: {t.sni}</div>}
                  </td>
                  <td>{t.uptime_24h_percent !== null ? `${t.uptime_24h_percent}%` : "—"}</td>
                  <td>
                    {t.last_checked_at
                      ? new Date(t.last_checked_at).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit", second: "2-digit" })
                      : "—"}
                  </td>
                  <td>
                    {t.recent_failures && t.recent_failures.length > 0 ? (
                      <div style={{ fontSize: 12 }}>
                        {t.recent_failures.slice(0, 2).map((f) => (
                          <div key={f.id} style={{ color: "#d32f2f" }}>
                            {new Date(f.checked_at).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })}: {f.error || "ошибка"}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span style={{ color: "green", fontSize: 12 }}>Нет падений</span>
                    )}
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: 6 }}>
                      <Button variant="secondary" onClick={() => handleProbe(t.id)}>
                        Проверить
                      </Button>
                      <Button variant="secondary" onClick={() => handleDelete(t.id, t.name)}>
                        ✕
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
